import { useCallback, useEffect, useRef, useState } from 'react';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DynamicAutocompleteFilterConfig,
  FilterCategoryItemProps,
  setFilterLabels,
  TableFilterOption,
} from '@/components/data-table/filters-v2';
import {
  SelectedOptionsSection,
  shouldShowSelectedSection,
} from '@/components/data-table/filters-v2/submenus/selected-options-section';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { DEFAULT_FILTERS_PAGE_SIZE } from '@/lib/constants/pagination';
import { cn, normalizeColorHex } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';

const TAG = 'DynamicAutocompleteFilterSubmenu:';

// Cache for filter options to prevent refetching on every dropdown open
const optionsCache = new Map<string, { options: TableFilterOption[]; cursor: number | null }>();

/**
 * Clears all optionsCache entries whose key starts with the given filter name.
 * Use after mutations that affect the entities shown in a dynamic filter dropdown,
 * so the next open triggers a fresh API fetch instead of showing stale data.
 *
 * @param filterName - The filter config `name` (e.g. "department", "role", "seniority")
 */
export const clearDynamicFilterOptionsCache = (filterName: string): void => {
  for (const key of optionsCache.keys()) {
    if (key === filterName || key.startsWith(`${filterName}:`)) {
      optionsCache.delete(key);
    }
  }
};

/**
 * Submenu content for dynamic autocomplete filter (loads options from API)
 */
export function DynamicAutocompleteFilterSubmenu({
  config,
  activeValues,
  onFilterChange,
  onClearFilter,
  onCloseSubmenu,
  activeFilters,
}: FilterCategoryItemProps & {
  config: DynamicAutocompleteFilterConfig;
  onCloseSubmenu?: () => void;
}) {
  const { t } = useTranslation('common');
  const selected = new Set(activeValues);
  const [options, setOptions] = useState<TableFilterOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // `searchValue` is the live, immediate input value (keeps typing responsive).
  // `committedSearch` is the debounced value that actually drives the cache key,
  // the load effect, and the API request — so typing fires ONE request after the
  // user pauses instead of one per keystroke.
  const [searchValue, setSearchValue] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  // Use ref for cursor to avoid stale closure issues
  const cursorRef = useRef<number | null>(null);
  const isLoadingMoreRef = useRef(false);

  // Minimum characters before triggering a search (0 = always load)
  const minSearchChars = config.minSearchChars ?? 0;

  // Static pinned options (e.g. "No department"), hidden during an active search.
  // Their labels are registered up-front so chips can render them immediately.
  const topOptions = config.topOptions ?? [];
  useEffect(() => {
    if (topOptions.length > 0) {
      setFilterLabels(config.name, topOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.name]);
  const visibleTopOptions = committedSearch ? [] : topOptions;

  // Resolve a dependent filter value (e.g. City scoped by selected Country).
  // Treat an empty array as undefined so the dependent filter loads ALL results
  // when its dependency is cleared (otherwise we'd send e.g. `country: []`).
  const rawDependentValue = config.dependsOn ? activeFilters?.[config.dependsOn] : undefined;
  const dependentValue =
    Array.isArray(rawDependentValue) && rawDependentValue.length === 0
      ? undefined
      : rawDependentValue;
  const dependentKey = Array.isArray(dependentValue)
    ? dependentValue.join(',')
    : (dependentValue ?? '');

  // Cache key for this filter (includes the DEBOUNCED search and any dependency
  // value). Using the committed (debounced) value keeps the load effect below
  // from firing on every keystroke.
  const cacheKey = [config.name, dependentKey, committedSearch].filter(Boolean).join(':');

  // Load options from API
  const loadOptions = useCallback(
    async (loadMoreCursor: number | null = null) => {
      const isLoadMore = loadMoreCursor !== null;

      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        cursorRef.current = null;
      }

      try {
        const response = await config.loadOptions({
          searchString: committedSearch || undefined,
          cursor: loadMoreCursor,
          pageSize: DEFAULT_FILTERS_PAGE_SIZE,
          // Pass the dependent filter value (e.g. selected country) when configured
          ...(config.dependsOn && dependentValue ? { [config.dependsOn]: dependentValue } : {}),
        });

        const mappedOptions = response.items.map((item: any) => ({
          label: item.name ?? item.label ?? '',
          value: item.id != null ? String(item.id) : (item.value ?? ''),
          color: normalizeColorHex(item.colorHex) ?? item.color ?? undefined,
          // Map avatar - handle all possible field names across different entities
          // Team members: avatarLink, profileImageUrl, avatarUrl
          // Projects/Clients: logoUrl, avatarLink
          avatar:
            item.avatarLink ??
            item.profileImageUrl ??
            item.avatarUrl ??
            item.logoUrl ??
            item.avatar ??
            null,
          // Optional secondary metadata used by custom label renderers (e.g. member status)
          status: item.status ?? null,
          // Scheduled archive/unarchive dates — used by the project label renderer to
          // show an "Archived" badge and a scheduled-change warning tooltip.
          nextArchiveDate: item.nextArchiveDate ?? null,
          nextUnarchiveDate: item.nextUnarchiveDate ?? null,
        }));

        // Update cursor ref immediately
        cursorRef.current = response.pagination.cursor;
        setHasMore(response.pagination.cursor !== null);

        if (isLoadMore) {
          setOptions(prev => {
            const newOptions = [...prev, ...mappedOptions];
            // Update cache
            optionsCache.set(cacheKey, {
              options: newOptions,
              cursor: response.pagination.cursor,
            });
            // Cache labels for chips display
            setFilterLabels(config.name, newOptions);
            return newOptions;
          });
        } else {
          setOptions(mappedOptions);
          // Update cache
          optionsCache.set(cacheKey, {
            options: mappedOptions,
            cursor: response.pagination.cursor,
          });
          // Cache labels for chips display
          setFilterLabels(config.name, mappedOptions);
        }
      } catch (error) {
        logger.error(TAG, 'Failed to load filter options:', error);
        if (!isLoadMore) {
          setOptions([]);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
      }
    },
    [config, committedSearch, cacheKey, dependentValue]
  );

  // Load whenever the cache key changes — first open, a DEBOUNCED search change,
  // or a dependency change (e.g. selecting/clearing Country re-scopes City).
  useEffect(() => {
    // Respect minSearchChars: when a search is active but below the threshold,
    // show nothing rather than firing an API call.
    if (committedSearch && committedSearch.length < minSearchChars) {
      setOptions([]);
      setHasMore(false);
      return;
    }

    const cached = optionsCache.get(cacheKey);
    if (cached && !committedSearch) {
      // Use cached data
      setOptions(cached.options);
      cursorRef.current = cached.cursor;
      setHasMore(cached.cursor !== null);
    } else {
      // Load from API
      loadOptions(null);
    }
  }, [cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Commit the typed value to `committedSearch` after the debounce. This is the
  // single point that drives the load — keystrokes update only the live input.
  const commitSearch = useDebouncedCallback((value: string) => {
    cursorRef.current = null;
    setHasMore(true);
    setCommittedSearch(value);
  }, config.debounceMs ?? 200);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    commitSearch(value);
  };

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    // Don't load more if: no ref, already loading, no more pages, or no cursor
    if (
      !listRef.current ||
      isLoadingMoreRef.current ||
      !hasMore ||
      cursorRef.current == null // checks both null and undefined
    ) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      // Capture cursor value now
      const currentCursor = cursorRef.current;
      // Double check cursor is valid before proceeding
      if (currentCursor == null) return;

      // Set loading flag BEFORE async call to prevent duplicates
      isLoadingMoreRef.current = true;
      // Set cursor to null immediately to prevent same page loading
      cursorRef.current = null;
      loadOptions(currentCursor);
    }
  }, [hasMore, loadOptions]);

  const toggle = (value: string) => {
    const newSelected = new Set(selected);
    if (selected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    onFilterChange(config.name, Array.from(newSelected));
  };

  const handleClearAll = () => {
    onClearFilter(config.name);
    onCloseSubmenu?.();
  };

  // Jump the option list back to the top selected section (footer count click).
  const scrollListToTop = () => {
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderLabel = (opt: TableFilterOption) => {
    if (config.renderLabel) {
      return config.renderLabel(opt.label, opt);
    }
    return <span className="text-sm">{opt.label}</span>;
  };

  // First-page load shows a centered spinner only while there are no options yet.
  // Subsequent (load-more) fetches show a spinner pinned to the bottom of the list.
  const isFirstPageLoading = isLoading && options.length === 0;

  const hasSearch = config.withSearch !== false;
  // Total options currently available to show. When more pages exist (`hasMore`),
  // the real total is unknown but definitely larger than what's loaded — treat it
  // as effectively "many" so the threshold check below shows the selected section.
  const loadedTotal = visibleTopOptions.length + options.length;
  const effectiveTotal = hasMore ? Number.POSITIVE_INFINITY : loadedTotal;

  // Selected values are ALSO pinned to their own section on top (hidden during an
  // active search — searching implies looking for something not yet selected).
  // Only worth showing once the list is long enough to be hard to scan.
  // The main list below still shows every option (selected + unselected); the two
  // views share `toggle`/`selected`, so a change in either place stays in sync.
  const showSelectedSection =
    !committedSearch && selected.size > 0 && shouldShowSelectedSection(hasSearch, effectiveTotal);
  const mainOptions = [...visibleTopOptions, ...options];

  return (
    <Command shouldFilter={false} className="w-[280px]">
      {config.withSearch !== false && (
        <CommandInput
          placeholder={t('filters.search', { value: config.label })}
          className="h-9"
          value={searchValue}
          onValueChange={handleSearchChange}
        />
      )}
      <CommandList ref={listRef} className="max-h-[240px] p-1" onScroll={handleScroll}>
        {isFirstPageLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : mainOptions.length === 0 && !showSelectedSection ? (
          <CommandEmpty>{t('noResultsFound')}</CommandEmpty>
        ) : (
          <CommandGroup>
            {showSelectedSection && (
              <SelectedOptionsSection
                filterName={config.name}
                selectedValues={activeValues}
                loadedOptions={options}
                topOptions={config.topOptions}
                onToggle={toggle}
                renderLabel={renderLabel}
              />
            )}
            {mainOptions.map(opt => {
              const isSelected = selected.has(opt.value);
              // Pinned options are plain text — entity renderers (avatars,
              // status badges) don't apply to a "No value" sentinel.
              const isPinnedOption = visibleTopOptions.some(top => top.value === opt.value);
              return (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className={cn(
                    'flex min-w-0 cursor-pointer items-center gap-2 overflow-hidden',
                    isSelected && 'bg-muted'
                  )}
                >
                  <Checkbox checked={isSelected} className="shrink-0" />
                  <Label className="flex min-w-0 flex-1 cursor-pointer overflow-hidden">
                    {isPinnedOption ? (
                      <span className="text-sm">{opt.label}</span>
                    ) : (
                      renderLabel(opt)
                    )}
                  </Label>
                </CommandItem>
              );
            })}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </CommandGroup>
        )}
      </CommandList>
      <CommandSeparator />
      <CommandGroup className="p-2">
        <div className="flex items-center justify-between">
          <Button size="sm" className="h-7" onClick={handleClearAll} disabled={selected.size === 0}>
            {t('filters.clearAll')}
          </Button>
          {/* Clicking the count scrolls the list back up to the selected section. */}
          <button
            type="button"
            onClick={scrollListToTop}
            disabled={selected.size === 0}
            className="rounded-sm text-xs text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
          >
            {t('filters.selected', { count: selected.size })}
          </button>
        </div>
      </CommandGroup>
    </Command>
  );
}
